import React, { ReactElement, useEffect, useState } from 'react';
import CcxComponentProps from '../../../core/CcxComponent';
import CmonSqlStat from '../../../types/CmonSqlStat';
import FriendlyTimestamp from '../../../types/FriendlyTimestamp';
import AppChart from '../../AppChart';
import ChartCustomTooltip from './ChartCustomTooltip';

interface Props extends CcxComponentProps {
    data?: CmonSqlStat[];
    loading: boolean;
    displaySummary?: boolean;
    height?: number;
    width?: number;
}

function PostgreSqlCommitsChart({
    data,
    loading,
    displaySummary,
    testId = 'PostgreSqlCommitsChart',
    width,
    height = 250,
}: Props): ReactElement {
    const [chartData, setChartData] = useState<any>([]);
    const [summary, setSummary] = useState<any>([]);

    const setChartSummary = (data: any, series: any) => {
        const max: number[] = [];
        const min: number[] = [];
        const sum: number[] = [];
        const avg: number[] = [];

        series.forEach((s: any) => {
            max[s.value] = 0;
            min[s.value] = 0;
            sum[s.value] = 0;
            avg[s.value] = 0;
        });

        data.forEach((d: any) => {
            series.forEach((s: any) => {
                sum[s.value] += d[s.value];

                min[s.value] =
                    d[s.value] < min[s.value] ? d[s.value] : min[s.value];
                max[s.value] =
                    d[s.value] > max[s.value] ? d[s.value] : max[s.value];
            });
        });

        series.forEach((s: any) => {
            avg[s.value] = sum[s.value] / data.length;
        });

        const dataSummary = series.map((s: any) => {
            return {
                serie: s.value,
                label: s.label,
                avg: avg[s.value].toFixed(),
                min: min[s.value].toFixed(),
                max: max[s.value].toFixed(),
                sum: sum[s.value].toFixed(),
            };
        });

        setSummary(dataSummary);
    };

    useEffect(() => {
        if (data && data.length > 0) {
            const formattedData = data.map((stat: CmonSqlStat) => {
                return [
                    {
                        yValue: stat.commits,
                        series: 'commits',
                        xValue: new FriendlyTimestamp(stat.created).getDate(),
                    },
                ];
            });
            setChartData(formattedData.flat());

            const summarySeries = [
                {
                    value: 'commits',
                    label: 'commits',
                },
            ];
            setChartSummary(data, summarySeries);
        }
    }, [data]);

    const chartConfig = {
        title: {
            visible: true,
            text: 'Commits',
        },
        description: {
            visible: true,
            text: '',
        },
        padding: 'auto',
        height,
        width,
        autoFit: !width && !height,
        xField: 'xValue',
        yField: 'yValue',
        xAxis: {
            type: 'time',
            mask: 'HH:mm',
        },
        yAxis: {
            label: {
                formatter: (text: string, item: any, index: number) => {
                    return text;
                },
            },
        },
        animation: false,
        seriesField: 'series',
        tooltip: {
            customContent: (title: any, data: any) => {
                const getTitle = () => {
                    if (!data || data.length === 0) return '';
                    return data[0]?.data?.xValue.toString();
                };

                const getContent = () => {
                    if (!data || data.length === 0) return [];
                    return data.map((d: any) => ({
                        color: d?.color,
                        label: d?.data?.series,
                        value: d?.data?.yValue,
                    }));
                };

                const tooltipData = {
                    title: getTitle(),
                    content: getContent(),
                };

                return (
                    <ChartCustomTooltip
                        title={tooltipData.title}
                        content={tooltipData.content}
                    />
                );
            },
        },
    };

    return (
        <AppChart
            type="area"
            data={chartData}
            config={chartConfig}
            summary={summary}
            loading={loading}
            displaySummary={displaySummary}
            testId={testId}
        />
    );
}

export default PostgreSqlCommitsChart;
